import moment from 'moment-timezone';

/**
 * Extracts time in minutes from midnight from a date value using the given timezone
 * Useful for comparing only time portions, ignoring the date
 * @param {Date|string} dateValue - The date/time value
 * @param {string} timezone - The timezone to interpret the time in
 * @returns {number} - Minutes from midnight (0-1439)
 */
export const getTimeInMinutes = (dateValue, timezone) => {
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(dateValue, tz);
  return m.hour() * 60 + m.minute();
};

/**
 * Combines date and time values for event scheduling
 * Handles all-day events, timezone conversions, and midnight-spanning events
 *
 * @param {Object} params - Parameters object
 * @param {string|Date} params.selectedDate - The date selected (from date picker)
 * @param {string|Date} params.meetingStartTime - Start time (from time picker or full datetime)
 * @param {string|Date} params.meetingEndTime - End time (from time picker or full datetime)
 * @param {boolean} params.isAllDay - Whether this is an all-day event
 * @param {string} params.timezone - User's timezone (defaults to browser timezone)
 * @returns {Object} - { formattedStartTime, formattedEndTime }
 */
export const combineDateAndTime = ({ selectedDate, meetingStartTime, meetingEndTime, isAllDay = false, timezone = null }) => {
  const tz = timezone || moment.tz.guess();

  if (isAllDay) {
    // For all-day events: just use the dates
    return {
      formattedStartTime: moment.tz(meetingStartTime, tz).format('YYYY-MM-DD'),
      formattedEndTime: moment.tz(meetingEndTime, tz).format('YYYY-MM-DD'),
    };
  }

  // For non-all-day events: combine date with time
  // Get the date from selectedDate (if provided) or from meetingStartTime
  const dateToUse = selectedDate;
  const selectedDateMoment = moment.tz(dateToUse, tz);

  // Get the time from meeting_start_time and meeting_end_time
  const startTime = moment.tz(meetingStartTime, tz);
  const endTime = moment.tz(meetingEndTime, tz);

  // Combine date with start time
  const combinedStartTime = moment.tz(
    {
      year: selectedDateMoment.year(),
      month: selectedDateMoment.month(),
      date: selectedDateMoment.date(),
      hour: startTime.hour(),
      minute: startTime.minute(),
      second: startTime.second(),
      millisecond: 0,
    },
    tz
  );

  // Combine date with end time
  let combinedEndTime = moment.tz(
    {
      year: selectedDateMoment.year(),
      month: selectedDateMoment.month(),
      date: selectedDateMoment.date(),
      hour: endTime.hour(),
      minute: endTime.minute(),
      second: endTime.second(),
      millisecond: 0,
    },
    tz
  );

  // Check if end time is before or equal to start time (indicates next day)
  if (combinedEndTime.isSameOrBefore(combinedStartTime)) {
    combinedEndTime.add(1, 'day');
  }

  return {
    formattedStartTime: combinedStartTime.toDate(),
    formattedEndTime: combinedEndTime.toDate(),
  };
};

/**
 * Interprets compact digit-only time as HHMM in 24-hour style (e.g. "1000" → 10:00, "1700" → 17:00).
 * Non-digit characters are stripped first (e.g. "17 00" → 17:00; "10:00" → 10:00). Requires exactly four digits after strip
 * so partial input like "100" does not commit while the user is still typing "1000".
 *
 * @param {string} raw - Raw input string
 * @returns {{ hour: number, minute: number } | null} Wall clock in 24h, or null if not a supported pattern
 */
export function parseCompactTimeDigits(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  const digits = raw.trim().replace(/\D/g, '');
  if (digits.length !== 4) return null;

  const hour = parseInt(digits.slice(0, 2), 10);
  const minute = parseInt(digits.slice(2, 4), 10);

  if (Number.isNaN(hour) || Number.isNaN(minute) || minute < 0 || minute > 59 || hour < 0 || hour > 23) {
    return null;
  }

  return { hour, minute };
}

/**
 * True if the string carries a 12h meridiem marker (case-insensitive), not as an arbitrary substring.
 */
function hasMeridiemMarker(s) {
  const t = s.trim();
  // "… : MM am/pm" or "… : M am/pm"
  if (/:\s*\d{1,2}\s*(am|pm|a\.m\.|p\.m\.)\s*$/i.test(t)) return true;
  // "… digit(s) then am/pm" e.g. 0130pm, 130PM
  if (/\d\s*(am|pm|a\.m\.|p\.m\.)\s*$/i.test(t)) return true;
  // "… space am/pm" at end
  if (/\s(am|pm|a\.m\.|p\.m\.)\s*$/i.test(t)) return true;
  return false;
}

function wall12To24(h12, minute, merToken) {
  if (Number.isNaN(h12) || Number.isNaN(minute) || minute < 0 || minute > 59 || h12 < 1 || h12 > 12) {
    return null;
  }
  const mer = String(merToken).toLowerCase();
  const isPm = mer.startsWith('p');
  let hour24;
  if (isPm) {
    hour24 = h12 === 12 ? 12 : h12 + 12;
  } else {
    hour24 = h12 === 12 ? 0 : h12;
  }
  return { hour: hour24, minute };
}

/**
 * Parses time from the Calendar text input on blur.
 * - If the text includes AM/PM (any common case, e.g. pm, PM, p.m.), it is interpreted as **12-hour** wall time.
 * - Otherwise it falls back to **24-hour** compact {@link parseCompactTimeDigits} (HHMM after stripping non-digits).
 *
 * @param {string} raw - Raw input string
 * @returns {{ hour: number, minute: number } | null} Wall clock in 24h, or null
 */
export function parseTimeBlurInput(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  const trimmed = raw
    .trim()
    .replace(/\u00a0/g, ' ')
    .replace(/[\u2000-\u200B\uFEFF]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!trimmed) return null;

  const mer = '(am|pm|a\\.m\\.|p\\.m\\.)';

  // 12h: H:MM or H:M + meridiem (case-insensitive)
  let m = trimmed.match(new RegExp(`^(\\d{1,2})\\s*:\\s*(\\d{1,2})\\s*${mer}$`, 'i'));
  if (m) {
    const out = wall12To24(parseInt(m[1], 10), parseInt(m[2], 10), m[3]);
    if (out) return out;
  }

  // 12h: HMM or HHMM directly before meridiem, no colon (e.g. 0130pm, 130PM)
  m = trimmed.match(new RegExp(`^(\\d{1,2})(\\d{2})\\s*${mer}$`, 'i'));
  if (m) {
    const out = wall12To24(parseInt(m[1], 10), parseInt(m[2], 10), m[3]);
    if (out) return out;
  }

  // 12h: exactly two-digit hour + two-digit minute + meridiem with optional space
  m = trimmed.match(new RegExp(`^(\\d{2})(\\d{2})\\s*${mer}$`, 'i'));
  if (m) {
    const out = wall12To24(parseInt(m[1], 10), parseInt(m[2], 10), m[3]);
    if (out) return out;
  }

  if (hasMeridiemMarker(trimmed)) {
    // Meridiem present but no supported 12h shape — do not misread as 24h
    return null;
  }

  return parseCompactTimeDigits(trimmed);
}
